/**
 * Card-related helper functions for tracking and observing card readiness
 */

/**
 * Observer-based card readiness checker
 * Watches for all cards matching a selector to have data-card-ready="true" before triggering callback
 *
 * @param {string} selector - CSS selector to find target cards
 * @param {Function} callback - Function to call when all cards are ready
 * @param {string} debugName - Name for debug logging (default: 'cards')
 * @param {string|string[]|Element|Element[]|null} containers - Container(s) to observe. Can be:
 *   - CSS selector string: '.front-page-content'
 *   - Array of CSS selectors: ['.front-page-content', '.sidebar']
 *   - DOM element: document.querySelector('.main')
 *   - Array of DOM elements: [element1, element2]
 *   - null/undefined: defaults to document.body
 * @returns {MutationObserver|null} - The observer instance or null if no cards found
 */
const createCardReadinessObserver = (selector, callback, debugName = 'cards', containers = null) => {
  // Resolve containers to an array of DOM elements
  const resolveContainers = (containerInput) => {
    if (!containerInput) return [document.body];

    // Convert to array if not already
    const containerArray = Array.isArray(containerInput) ? containerInput : [containerInput];
    const resolvedContainers = [];

    containerArray.forEach((container) => {
      if (typeof container === 'string') {
        // CSS selector string
        const elements = document.querySelectorAll(container);
        resolvedContainers.push(...Array.from(elements));
      } else if (container && container.nodeType === Node.ELEMENT_NODE) {
        // DOM element
        resolvedContainers.push(container);
      }
    });

    // Return empty array if no valid containers found (don't fallback to document.body)
    // This means the containers don't exist, so no cards need observing
    return resolvedContainers;
  };

  const observerContainers = resolveContainers(containers);

  // If no containers exist, no need to observe anything
  if (containers && observerContainers.length === 0) {
    console.log(`[${debugName}] No containers found for: ${Array.isArray(containers) ? containers.join(', ') : containers}. No observation needed.`);
    callback();
    return null;
  }
  const targetCards = Array.from(document.querySelectorAll(selector));
  let hasTriggered = false;
  let observer = null;
  let timeoutId = null;

  if (targetCards.length === 0) {
    callback();
    return null;
  }

  // Only log container info if there are issues or in development
  // const containerInfo = observerContainers.map(c =>
  //   c === document.body ? 'document.body' : (c.className || c.tagName)
  // ).join(', ');
  // console.log(`[${debugName}] Watching ${targetCards.length} cards in containers: ${containerInfo}`);

  // Create a Set for O(1) lookup of target cards
  const targetCardsSet = new Set(targetCards);

  // Track which cards we've already counted as ready (prevents double-counting)
  const readyCardsSet = new Set();

  // Count initially ready cards
  targetCards.forEach((card) => {
    if (card.getAttribute('data-card-ready') === 'true') {
      readyCardsSet.add(card);
    }
  });

  // Debug log for initial state
  // console.log(`[${debugName}] Initially ready: ${readyCardsSet.size}/${targetCards.length}`);

  const cleanup = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const triggerCallback = (reason = 'all ready') => {
    if (hasTriggered) return;
    hasTriggered = true;
    console.log(`[${debugName}] ${reason}, triggering callback`);
    cleanup();
    callback();
  };

  // If all cards are already ready, trigger immediately
  if (readyCardsSet.size >= targetCards.length) {
    triggerCallback('All cards already ready');
    return null;
  }

  const checkCardReady = (card) => {
    if (!targetCardsSet.has(card)) return; // Not one of our target cards
    if (readyCardsSet.has(card)) return; // Already counted this card

    const isReady = card.getAttribute('data-card-ready') === 'true';
    if (isReady) {
      readyCardsSet.add(card); // Mark this specific card as ready
      // console.log(`[${debugName}] Card ready: ${readyCardsSet.size}/${targetCards.length}`);

      if (readyCardsSet.size >= targetCards.length) {
        triggerCallback();
      }
    }
  };

  // Use observer(s) on specified containers for better performance
  // Multiple containers allow targeting specific page areas like:
  // ['.front-page-content', '.sidebar-content'] instead of document.body
  observer = new MutationObserver((mutations) => {
    if (hasTriggered) return;

    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-card-ready') {
        checkCardReady(mutation.target);
      }
    });
  });

  // Observe all resolved containers with subtree to catch all changes
  observerContainers.forEach((container) => {
    observer.observe(container, {
      attributes: true,
      attributeFilter: ['data-card-ready'],
      subtree: true,
    });
  });

  // Timeout to handle slow environments and edge cases
  timeoutId = setTimeout(() => {
    if (!hasTriggered) {
      console.warn(`[${debugName}] Timeout reached (${readyCardsSet.size}/${targetCards.length} ready), triggering callback anyway`);
      triggerCallback('Timeout reached');
    }
  }, 7000); // 7 seconds to accommodate slower development environments like my old computer

  return observer;
};

export default createCardReadinessObserver;
